import Chip, { ChipProps } from "@mui/material/Chip";
import { alpha, Theme } from "@mui/material/styles";

/**
 * Semantic color roles for a {@link StatusChip}, per the design-system chip spec
 * (.cursor/rules/design-system.mdc → Chips). These map to MUI's semantic palette
 * — use them, not our `encampGreen`/`encampRed` brand colors.
 *
 * `default` is the neutral grey role for non-semantic states (e.g. "no change"),
 * so callers route through StatusChip instead of dropping to a bare MUI `Chip`.
 */
export type StatusChipColor = "info" | "success" | "error" | "warning" | "default";

/**
 * The two spec variants:
 *   - `soft` — colored label on the role's `main` at 8% alpha, no border. The
 *     default; far easier on the eyeballs than MUI's solid colored chips.
 *   - `outlined` — colored 1px border + colored label, transparent fill (native MUI).
 */
export type StatusChipVariant = "soft" | "outlined";

export interface StatusChipProps extends Omit<ChipProps, "color" | "variant"> {
  color: StatusChipColor;
  variant?: StatusChipVariant;
}

/**
 * The canonical chip for conveying state/category by color. Renders a
 * `<Chip size="small">` styled to the design-system spec — never MUI's default
 * solid dark-fill + white-text chip. Only colors change from stock MUI; size
 * proportions and text stay at MUI defaults.
 *
 * @example
 * <StatusChip color="error" label="Missing chemical" />
 * <StatusChip color="success" variant="outlined" label="created" />
 */
export function StatusChip({
  color,
  variant = "soft",
  size = "small",
  sx,
  ...rest
}: StatusChipProps) {
  const hoverBackgroundColor = (theme: Theme) =>
    alpha(color === "default" ? theme.palette.text.primary : theme.palette[color].main, 0.2);

  const backgroundColor = (theme: Theme) =>
    alpha(color === "default" ? theme.palette.common.white : theme.palette[color].main, 0.08);

  if (variant === "outlined") {
    return (
      <Chip
        color={color}
        variant="outlined"
        size={size}
        sx={[
          {
            backgroundColor: backgroundColor,
            color: `${color}.main`,
            "&:hover": { backgroundColor: hoverBackgroundColor },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      />
    );
  }
  // Soft variant: light-filled, no border. `default` has no semantic palette
  // entry, so it derives a neutral grey from the text color instead.
  return (
    <Chip
      size={size}
      sx={[
        {
          color: color === "default" ? "text.secondary" : `${color}.main`,
          bgcolor: backgroundColor,
          "&:hover": { backgroundColor: hoverBackgroundColor },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );
}
